import { useInsertionEffect } from "react"

import { motion } from "framer-motion"
import isNull from "lodash-es/isNull"
import some from "lodash-es/some"
import { type MotionNodeAnimationOptions } from "motion-dom"
import { useFormContext } from "react-hook-form"
import { Trans } from "react-i18next"

import getByFieldName from "$/utils/get-by-field-name"

import {
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import { buildCssClassName } from "@/3514/utils"
import { ReflectionComponentType } from "@/constants"
import { SURVEY_STAGE, Survey as SurveyClass } from "@/entities/survey"
import { SurveyQuestion } from "@/entities/survey/models"
import DividerLine from "@/shared/divider/DividerLine"
import FreeResponseComponent from "@/shared/free-response/FreeResponseComponent"
import { LikertComponent } from "@/shared/likert/new-liker.tsx/NewLikertComponent"
import { ERichTextRendererMode, RichTextRenderer } from "@/shared/rich-text-renderer"
import type { TEmptyCallback } from "@/shared/types/functions"

type Props = {
    survey: SurveyClass
}

const titleEditorId: string = "sht-ed"

function animateBlock(i: number): Partial<MotionNodeAnimationOptions> {
    return {
        initial: { opacity: 0, y: 12 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.3, ease: "easeOut", delay: i * 0.07 }
    }
}

export const Survey = ({ survey }: Props) => {
    const {
        register,
        control,
        formState: { errors }
    } = useFormContext()

    useInsertionEffect((): TEmptyCallback => {
        // temp quick solution
        const styleElement: HTMLStyleElement = document.createElement("style")

        styleElement.textContent = `
          p[data-placeholder] { margin: 0 !important; text-align: center !important; }
        `
        document.head.appendChild(styleElement)

        return (): void => (document.head.removeChild(styleElement), void 0)
    }, [])

    // @ts-expect-error wrong type for surveyStage, legacy stuff
    const isFinalSurvey: boolean = survey?.surveyStage?.order === SURVEY_STAGE.FINAL
    // @ts-expect-error wrong type for surveyStage, legacy stuff
    const isInterimSurvey: boolean = survey?.surveyStage?.order === SURVEY_STAGE.INTERIM

    const questionItemCn: string = isFinalSurvey || isInterimSurvey ? "mb-[60px]" : "mb-[50px]"

    return (
        <div>
            <motion.div {...animateBlock(0)}>
                <Typography
                    text={survey?.surveyTemplate?.headerText ?? ""}
                    className="mt-5"
                    tag={ETypographyTag.Heading1}
                    color={ETypographyColor.Black}
                    alignment={ETypographyAlignment.Center}
                    weight={ETypographyWeight.Medium}
                    size={ETypographySize.Heading1}
                    family={ETypographyFamily.DmSerif}
                />
            </motion.div>

            {survey?.surveyTemplate?.bodyText && (
                <motion.div {...animateBlock(1)}>
                    <RichTextRenderer
                        mode={ERichTextRendererMode.View}
                        withLegacyStyles
                        content={survey.surveyTemplate.bodyText}
                        cssConfig={{
                            classNameIdentifier: titleEditorId,
                            editorClassName: "flex mx-auto w-fit"
                        }}
                    />
                </motion.div>
            )}

            {some<SurveyQuestion>(survey.questions, (q: SurveyQuestion): boolean => !isNull(q.previousAnswer)) && (
                <motion.div {...animateBlock(2)}>
                    <p className="text-center mt-[50px]">
                        <Trans
                            i18nKey="yourPreviousSurveyResponses"
                            components={{
                                1: <span className="color-accent font-extrabold" />
                            }}
                        />
                    </p>
                </motion.div>
            )}

            <DividerLine fullWith className="my-5" />

            <motion.div {...animateBlock(3)}>
                {survey.questions.map((question, index) => (
                    <div className={questionItemCn} key={question.id}>
                        {question.isFreeResponse ? (
                            <FreeResponseComponent
                                questionNumber={index + 1}
                                register={register}
                                item={{
                                    question: question.questionData,
                                    prompt: question.freeResponsePrompt
                                }}
                                name={`${question.id}.free_response`}
                                error={getByFieldName(errors, `${question.id}.free_response`)?.message}
                                errors={errors}
                                questionStyle="one-line"
                                sampleResponse={question.sampleResponse}
                            />
                        ) : (
                            <LikertComponent
                                item={question.answerOptions}
                                question={question.questionData}
                                name={`${question.id}.answer`}
                                error={getByFieldName(errors, `${question.id}.answer`)?.message}
                                className={buildCssClassName(
                                    "stakeholder-survey__likert",
                                    isFinalSurvey || isInterimSurvey ? "mb-[60px]" : "mb-[50px]"
                                )}
                                previousAnswer={question.previousAnswer}
                                questionNumber={index + 1}
                                color="blank"
                                register={register}
                                control={control}
                                errors={errors}
                                questionStyle="one-line"
                                likertType={ReflectionComponentType.LIKERT_GROUP}
                            />
                        )}
                    </div>
                ))}
            </motion.div>
        </div>
    )
}
